/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

import { IRequestContext } from '@msdyn365-commerce/core';
import { ChannelConfiguration, ProductPrice } from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';
import { reaction } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';
import Input from 'reactstrap/lib/Input';
import Price from '../../components/Price';
import getKeyedInPrice, { createKeyedInPrice } from '../../dataActions/get-keyed-in-price';
import getKeyedInPriceError, { createKeyedInPriceError } from '../../dataActions/get-keyed-in-price-error';
import getPriceForSelectedVariant, { PriceForSelectedVariantInput } from './data-actions/get-price-for-selected-variant';
import { IPrice, IPriceRange, IProductPriceData } from './product-price.data';
import { IProductPriceProps } from './product-price.props.autogenerated';

/**
 *
 * ProductPrice component
 * @extends {React.Component<IProductPriceProps<IProductPriceData>>}
 */
@observer
class ProductPriceModule extends React.Component<IProductPriceProps<IProductPriceData>> {
    public async componentDidMount(): Promise<void> {
        reaction(
            () => this.props.data.product.result ? this.props.data.product.result.RecordId : null,
            () => {
                if (this.props.context &&
                    this.props.data.product.result &&
                    this.props.config.priceDisplayType === 'affiliationPrice' &&
                    (this.props.config.affiliationId || this.props.context.app.config.wineClubSetup.affiliationId)) {
                    const actionInput = new PriceForSelectedVariantInput(
                        this.props.data.product.result.RecordId,
                        // @ts-ignore
                        +this.props.context.actionContext.requestContext.apiSettings.channelId,
                        this.props.data.product.result,
                        this.props.context.request.user.customerAccountNumber,
                        this.props.config.affiliationId || this.props.context.app.config.wineClubSetup.affiliationId
                    );

                    getPriceForSelectedVariant(actionInput, this.props.context.actionContext)
                        .catch(error => {
                            this.props.telemetry.warning('Unable to update Price for Product');
                            this.props.telemetry.error(error);
                        });
                }
            }
        );
    }

    public render(): JSX.Element | null {
        if (this.props.config.loginGated && !this.props.context.request.user.isAuthenticated) {
            return null;
        }

        let content: IProductPriceData;
        try {
            content = this.props.data;
        } catch (e) {
            this.props.telemetry.error(`Something went wrong ------${e}`);
            return <div>{e}</div>;
        }

        const priceData = this._mapPriceFromSimpleProductAction(
            content.productPrice.result,
            content.channelConfiguration.result,
            this.props.context.request);

        const product = this.props.data.product.result;
        const isPriceKeyedIn = product && product.Behavior && product.Behavior.KeyInPriceValue;
        if (isPriceKeyedIn) {
            return (
                <div className='product-price keyed-in'>
                    {this._renderKeyedInPrice()}
                </div>
            );
        }
        return (
            <div className='product-price'>
                {this._getFormattedPrice(priceData, this.props.context.cultureFormatter.formatCurrency)}
            </div>
        );
    }

    private _onKeyedInPriceChange = (targetPrice: React.ChangeEvent<HTMLInputElement>) => {
        const priceInput = Number(targetPrice && targetPrice.target && targetPrice.target.value);
        void getKeyedInPrice(createKeyedInPrice(priceInput), this.props.context.actionContext);

        if (priceInput < 25 || priceInput > 500) {
            void getKeyedInPriceError(createKeyedInPriceError('Please enter a price between $25 and $500.'), this.props.context.actionContext);
        } else {
            void getKeyedInPriceError(createKeyedInPriceError(''), this.props.context.actionContext);
        }
    };

    private _renderKeyedInPrice = () => {
        let price: number | undefined = this.props.data.keyedInPrice.result;
        if (!price || isNaN(price)) {
            price = undefined;
        }
        return (
            <div className='product-price'>
                <label hidden htmlFor='Price'>Price</label>
                <Input
                    id='Price'
                    type='number'
                    min='0'
                    value={price || ''}
                    aria-live='polite'
                    aria-label={'Price input'}
                    onChange={this._onKeyedInPriceChange}
                />
            </div>
        );
    };

    private _getFormattedPrice(priceData: IPrice | null, currencyFormatter: (price: string | number, currencyCode?: string) => string): JSX.Element | null {
        if (!priceData) {
            return null;
        }
        const { currentPrice, originalPrice } = priceData;
        return (
            <Price
                locale={priceData.locale}
                currencyCode={priceData.currencyCode}
                freePriceText={priceData.freePriceText}
                currentPrice={
                    currentPrice
                        ? {
                            startingValue: currentPrice.startingValue,
                            endingValue: currentPrice.endingValue
                        }
                        : undefined
                }
                originalPrice={
                    originalPrice
                        ? {
                            startingValue: originalPrice.startingValue,
                            endingValue: originalPrice.endingValue
                        }
                        : undefined
                }
                rangeFormatText={priceData.rangeFormatText}
                savingsText={priceData.savingsText}
            />
        );
    }

    private _mapPriceFromSimpleProductAction = (productPrice: ProductPrice | undefined, channelConfiguration: ChannelConfiguration | undefined, requestContext: IRequestContext | undefined): IPrice | null => {
        if (productPrice && requestContext) {
            let priceType;
            if (this.props.config.priceDisplayType === 'affiliationPrice' || this.props.config.priceDisplayType === 'currentPrice') {
                priceType = this._mapNumberToIPriceRange(productPrice.CustomerContextualPrice);
            } else if (this.props.config.priceDisplayType === 'basePrice') {
                priceType = this._mapNumberToIPriceRange(productPrice.BasePrice || 0);
            }

            // if price type wasnt set, then set it to the default type used previously
            if (!priceType) {
                // AKA 'currentPrice' - this is by default
                priceType = this._mapNumberToIPriceRange(Math.max(productPrice.BasePrice || 0, productPrice.TradeAgreementPrice || 0, productPrice.AdjustedPrice || 0));
            }

            const originalPrice = priceType;
            const currentPrice = priceType;

            return {
                locale: requestContext.locale,
                currencyCode: (channelConfiguration && channelConfiguration.Currency) || 'USD',
                freePriceText: this.props.resources.priceFree,
                currentPrice,
                originalPrice
            };
        }

        return null;
    };

    private _mapNumberToIPriceRange = (price: number | undefined): IPriceRange | undefined => {
        if (price !== undefined) {
            return {
                startingValue: price
            };
        }

        return undefined;
    };
}

export default ProductPriceModule;
